// File type detection and thumbnail generation utilities

export const getFileIcon = (fileType, fileName) => {
  const extension = fileName.toLowerCase().split('.').pop();
  
  // Define file type categories and their corresponding enhanced SVG thumbnails
  const fileIcons = {
    // Images - Camera/Photo icon with modern gradient
    image: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="imageGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#E3F2FD"/>
            <stop offset="100%" style="stop-color:#BBDEFB"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#imageGrad)" stroke="#1976D2" stroke-width="1"/>
        <rect x="8" y="12" width="24" height="18" rx="2" fill="#1976D2" opacity="0.1"/>
        <rect x="8" y="12" width="24" height="18" rx="2" stroke="#1976D2" stroke-width="1.5" fill="none"/>
        <circle cx="26" cy="18" r="2" fill="#1976D2"/>
        <path d="M8 24L14 20L20 22L32 16V30H8V24Z" fill="#1976D2" opacity="0.6"/>
        <circle cx="20" cy="6" r="1.5" fill="#1976D2"/>
        <rect x="17" y="4" width="6" height="3" rx="1.5" fill="#1976D2" opacity="0.3"/>
      </svg>
    `)}`,
    
    // PDF - Document with red accent and layers
    pdf: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="pdfGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FFEBEE"/>
            <stop offset="100%" style="stop-color:#FFCDD2"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#pdfGrad)" stroke="#D32F2F" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#D32F2F" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#D32F2F" stroke-width="1.5" fill="none"/>
        <rect x="13" y="16" width="14" height="1" fill="#D32F2F" opacity="0.6"/>
        <rect x="13" y="19" width="10" height="1" fill="#D32F2F" opacity="0.4"/>
        <rect x="13" y="22" width="12" height="1" fill="#D32F2F" opacity="0.4"/>
        <text x="20" y="28" font-family="Arial, sans-serif" font-size="6" font-weight="bold" fill="#D32F2F" text-anchor="middle">PDF</text>
      </svg>
    `)}`,
    
    // Word - Document with blue accent and text lines
    word: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="wordGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#E3F2FD"/>
            <stop offset="100%" style="stop-color:#BBDEFB"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#wordGrad)" stroke="#2196F3" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#2196F3" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#2196F3" stroke-width="1.5" fill="none"/>
        <rect x="13" y="15" width="14" height="1" fill="#2196F3" opacity="0.7"/>
        <rect x="13" y="17" width="12" height="1" fill="#2196F3" opacity="0.5"/>
        <rect x="13" y="19" width="15" height="1" fill="#2196F3" opacity="0.7"/>
        <rect x="13" y="21" width="10" height="1" fill="#2196F3" opacity="0.5"/>
        <rect x="13" y="23" width="13" height="1" fill="#2196F3" opacity="0.7"/>
        <text x="20" y="29" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#2196F3" text-anchor="middle">WORD</text>
      </svg>
    `)}`,
    
    // Excel - Spreadsheet with green grid
    excel: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="excelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#E8F5E8"/>
            <stop offset="100%" style="stop-color:#C8E6C9"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#excelGrad)" stroke="#4CAF50" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#4CAF50" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#4CAF50" stroke-width="1.5" fill="none"/>
        <!-- Grid lines -->
        <line x1="13" y1="16" x2="27" y2="16" stroke="#4CAF50" stroke-width="0.5"/>
        <line x1="13" y1="19" x2="27" y2="19" stroke="#4CAF50" stroke-width="0.5"/>
        <line x1="13" y1="22" x2="27" y2="22" stroke="#4CAF50" stroke-width="0.5"/>
        <line x1="16" y1="14" x2="16" y2="24" stroke="#4CAF50" stroke-width="0.5"/>
        <line x1="20" y1="14" x2="20" y2="24" stroke="#4CAF50" stroke-width="0.5"/>
        <line x1="24" y1="14" x2="24" y2="24" stroke="#4CAF50" stroke-width="0.5"/>
        <rect x="13" y="14" width="14" height="10" fill="none" stroke="#4CAF50" stroke-width="1"/>
        <text x="20" y="29" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#4CAF50" text-anchor="middle">EXCEL</text>
      </svg>
    `)}`,
    
    // PowerPoint - Slides with orange accent
    powerpoint: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="pptGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FFF3E0"/>
            <stop offset="100%" style="stop-color:#FFE0B2"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#pptGrad)" stroke="#FF9800" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#FF9800" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#FF9800" stroke-width="1.5" fill="none"/>
        <rect x="13" y="15" width="14" height="8" rx="1" fill="#FF9800" opacity="0.1" stroke="#FF9800" stroke-width="1"/>
        <rect x="15" y="17" width="4" height="1" fill="#FF9800" opacity="0.6"/>
        <rect x="15" y="19" width="6" height="1" fill="#FF9800" opacity="0.6"/>
        <circle cx="25" cy="20" r="1" fill="#FF9800" opacity="0.6"/>
        <text x="20" y="29" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#FF9800" text-anchor="middle">PPT</text>
      </svg>
    `)}`,
    
    // Text files - Document with purple accent
    text: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="textGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#F3E5F5"/>
            <stop offset="100%" style="stop-color:#E1BEE7"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#textGrad)" stroke="#9C27B0" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#9C27B0" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#9C27B0" stroke-width="1.5" fill="none"/>
        <rect x="13" y="15" width="12" height="1" fill="#9C27B0" opacity="0.7"/>
        <rect x="13" y="17" width="14" height="1" fill="#9C27B0" opacity="0.7"/>
        <rect x="13" y="19" width="10" height="1" fill="#9C27B0" opacity="0.7"/>
        <rect x="13" y="21" width="13" height="1" fill="#9C27B0" opacity="0.7"/>
        <rect x="13" y="23" width="8" height="1" fill="#9C27B0" opacity="0.7"/>
        <text x="20" y="29" font-family="Arial, sans-serif" font-size="6" font-weight="bold" fill="#9C27B0" text-anchor="middle">TXT</text>
      </svg>
    `)}`,
    
    // Archive files - Folder with zipper
    archive: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="zipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FFF8E1"/>
            <stop offset="100%" style="stop-color:#F0F4C3"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#zipGrad)" stroke="#F57F17" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#F57F17" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#F57F17" stroke-width="1.5" fill="none"/>
        <!-- Zipper design -->
        <rect x="18" y="14" width="4" height="12" fill="#F57F17" opacity="0.2"/>
        <rect x="19" y="15" width="2" height="1" fill="#F57F17"/>
        <rect x="19" y="17" width="2" height="1" fill="#F57F17"/>
        <rect x="19" y="19" width="2" height="1" fill="#F57F17"/>
        <rect x="19" y="21" width="2" height="1" fill="#F57F17"/>
        <rect x="19" y="23" width="2" height="1" fill="#F57F17"/>
        <circle cx="20" cy="25" r="1" fill="#F57F17"/>
        <text x="20" y="31" font-family="Arial, sans-serif" font-size="6" font-weight="bold" fill="#F57F17" text-anchor="middle">ZIP</text>
      </svg>
    `)}`,
    
    // Video files - Play button with film strip
    video: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="videoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#E8EAF6"/>
            <stop offset="100%" style="stop-color:#C5CAE9"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#videoGrad)" stroke="#3F51B5" stroke-width="1"/>
        <rect x="8" y="12" width="24" height="16" rx="2" fill="#FFFFFF" stroke="#3F51B5" stroke-width="1.5"/>
        <rect x="6" y="14" width="2" height="2" fill="#3F51B5"/>
        <rect x="6" y="18" width="2" height="2" fill="#3F51B5"/>
        <rect x="6" y="22" width="2" height="2" fill="#3F51B5"/>
        <rect x="32" y="14" width="2" height="2" fill="#3F51B5"/>
        <rect x="32" y="18" width="2" height="2" fill="#3F51B5"/>
        <rect x="32" y="22" width="2" height="2" fill="#3F51B5"/>
        <path d="M18 16L24 20L18 24V16Z" fill="#3F51B5"/>
        <text x="20" y="35" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#3F51B5" text-anchor="middle">VIDEO</text>
      </svg>
    `)}`,
    
    // Audio files - Music note with sound waves
    audio: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="audioGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FCE4EC"/>
            <stop offset="100%" style="stop-color:#F8BBD9"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#audioGrad)" stroke="#E91E63" stroke-width="1"/>
        <path d="M16 8V24C16 26.2 14.2 28 12 28C9.8 28 8 26.2 8 24C8 21.8 9.8 20 12 20C13.1 20 14.1 20.4 14.8 21.1V12L24 10V20C24 22.2 22.2 24 20 24C17.8 24 16 22.2 16 20C16 17.8 17.8 16 20 16C21.1 16 22.1 16.4 22.8 17.1V8L16 8Z" fill="#E91E63"/>
        <path d="M28 14C29 15 29 16 28 17" stroke="#E91E63" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M30 12C32 14 32 18 30 20" stroke="#E91E63" stroke-width="1.5" stroke-linecap="round"/>
        <text x="20" y="35" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#E91E63" text-anchor="middle">AUDIO</text>
      </svg>
    `)}`,
    
    // Code files - Brackets with syntax highlighting
    code: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="codeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#E0F2F1"/>
            <stop offset="100%" style="stop-color:#B2DFDB"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#codeGrad)" stroke="#00695C" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#00695C" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#00695C" stroke-width="1.5" fill="none"/>
        <path d="M15 18L13 20L15 22" stroke="#00695C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M25 18L27 20L25 22" stroke="#00695C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="21" y1="17" x2="19" y2="23" stroke="#00695C" stroke-width="1.5" stroke-linecap="round"/>
        <text x="20" y="29" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#00695C" text-anchor="middle">CODE</text>
      </svg>
    `)}`,
    
    // Default/Generic file - Modern document icon
    default: `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="defaultGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FAFAFA"/>
            <stop offset="100%" style="stop-color:#E0E0E0"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="url(#defaultGrad)" stroke="#757575" stroke-width="1"/>
        <path d="M10 6H24L30 12V32C30 33.1 29.1 34 28 34H12C10.9 34 10 33.1 10 32V6Z" fill="#FFFFFF" stroke="#757575" stroke-width="1.5"/>
        <path d="M24 6V12H30" stroke="#757575" stroke-width="1.5" fill="none"/>
        <rect x="13" y="16" width="8" height="1" fill="#757575" opacity="0.5"/>
        <rect x="13" y="19" width="12" height="1" fill="#757575" opacity="0.3"/>
        <rect x="13" y="22" width="6" height="1" fill="#757575" opacity="0.3"/>
        <circle cx="20" cy="25" r="2" fill="none" stroke="#757575" stroke-width="1" opacity="0.5"/>
        <text x="20" y="32" font-family="Arial, sans-serif" font-size="5" font-weight="bold" fill="#757575" text-anchor="middle">FILE</text>
      </svg>
    `)}`
  };
  
  // Determine file category based on type or extension
  if (fileType.startsWith('image/')) {
    return fileIcons.image;
  } else if (fileType === 'application/pdf' || extension === 'pdf') {
    return fileIcons.pdf;
  } else if (fileType.includes('word') || ['doc', 'docx'].includes(extension)) {
    return fileIcons.word;
  } else if (fileType.includes('excel') || fileType.includes('spreadsheet') || ['xls', 'xlsx'].includes(extension)) {
    return fileIcons.excel;
  } else if (fileType.includes('powerpoint') || fileType.includes('presentation') || ['ppt', 'pptx'].includes(extension)) {
    return fileIcons.powerpoint;
  } else if (fileType.startsWith('text/') || ['txt', 'csv', 'md', 'json'].includes(extension)) {
    return fileIcons.text;
  } else if (fileType.includes('zip') || fileType.includes('rar') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    return fileIcons.archive;
  } else if (fileType.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return fileIcons.video;
  } else if (fileType.startsWith('audio/') || ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
    return fileIcons.audio;
  } else if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'scss', 'php', 'py', 'java', 'cpp', 'c', 'h', 'sql', 'xml', 'json'].includes(extension)) {
    return fileIcons.code;
  } else {
    return fileIcons.default;
  }
};

export const canPreviewFile = (fileType, fileName) => {
  const extension = fileName.toLowerCase().split('.').pop();
  
  // Define which file types can be previewed
  const previewableTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain', 'text/csv'
  ];
  
  const previewableExtensions = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 
    'pdf', 
    'doc', 'docx', 
    'xls', 'xlsx', 
    'ppt', 'pptx',
    'txt', 'csv'
  ];
  
  return previewableTypes.includes(fileType) || previewableExtensions.includes(extension);
};

export const getPreviewUrl = (file) => {
  // For images, return the URL directly
  if (file.type.startsWith('image/')) {
    return file.url;
  }
  
  // For PDFs, we can embed them or show in an iframe
  if (file.type === 'application/pdf') {
    return file.url; // PDF can be shown in iframe
  }
  
  // For Microsoft Office documents, use Office Online Viewer
  if (file.type.includes('word') || file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx')) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
  }
  
  if (file.type.includes('excel') || file.type.includes('spreadsheet') || 
      file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsx')) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
  }
  
  if (file.type.includes('powerpoint') || file.type.includes('presentation') || 
      file.name.toLowerCase().endsWith('.ppt') || file.name.toLowerCase().endsWith('.pptx')) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
  }
  
  // For text files, we could read and display the content
  if (file.type.startsWith('text/')) {
    return file.url; // Could be loaded and displayed as text
  }
  
  // For non-previewable files, return null
  return null;
}; 